
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Clock, Plus, Edit3, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface FollowUpStep {
  id: string;
  type: 'email' | 'task' | 'call';
  title: string;
  content: string;
  delayDays: number;
  template?: string;
}

interface FollowUpSequence {
  id: string;
  name: string;
  description: string;
  trigger: string;
  steps: FollowUpStep[];
  isActive: boolean;
  completionRate: number;
}

export const FollowUpSequenceManager: React.FC = () => {
  const [sequences, setSequences] = useState<FollowUpSequence[]>([
    {
      id: '1',
      name: 'New Candidate Onboarding',
      description: 'Welcome sequence for new candidates',
      trigger: 'candidate_added',
      steps: [
        {
          id: '1',
          type: 'email',
          title: 'Welcome Email',
          content: 'Welcome to our recruitment process...',
          delayDays: 0,
          template: 'welcome_candidate'
        },
        {
          id: '2',
          type: 'task',
          title: 'Initial Screening Call',
          content: 'Schedule and conduct initial screening',
          delayDays: 2
        },
        {
          id: '3',
          type: 'email',
          title: 'Follow-up Email',
          content: 'Thank you for the screening call...',
          delayDays: 3,
          template: 'post_screening_followup'
        }
      ],
      isActive: true,
      completionRate: 85
    },
    {
      id: '2',
      name: 'Client Engagement Sequence',
      description: 'Automated client follow-up sequence',
      trigger: 'client_inquiry',
      steps: [
        {
          id: '1',
          type: 'email',
          title: 'Initial Response',
          content: 'Thank you for your inquiry...',
          delayDays: 0,
          template: 'client_initial_response'
        },
        {
          id: '2',
          type: 'call',
          title: 'Discovery Call',
          content: 'Schedule discovery call to understand requirements',
          delayDays: 1
        }
      ],
      isActive: true,
      completionRate: 92
    }
  ]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<FollowUpSequence | null>(null);

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'call':
        return <Clock className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStepColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'call':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Follow-up Sequences</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Sequence
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Follow-up Sequence</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sequence Name</Label>
                  <Input placeholder="Enter sequence name" />
                </div>
                <div className="space-y-2">
                  <Label>Trigger Event</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select trigger" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate_added">Candidate Added</SelectItem>
                      <SelectItem value="client_inquiry">Client Inquiry</SelectItem>
                      <SelectItem value="interview_completed">Interview Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea placeholder="Describe this sequence" />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsDialogOpen(false)}>
                  Create Sequence
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {sequences.map((sequence) => (
          <Card key={sequence.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {sequence.name}
                    <Badge variant={sequence.isActive ? "default" : "secondary"}>
                      {sequence.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{sequence.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-medium">{sequence.completionRate}% completion</p>
                    <p className="text-xs text-muted-foreground">average rate</p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Trigger:</span>
                  <Badge variant="outline">{sequence.trigger}</Badge>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Sequence Steps</Label>
                  <div className="space-y-2">
                    {sequence.steps.map((step, index) => (
                      <div key={step.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                          <span className="text-sm font-medium">{index + 1}</span>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={getStepColor(step.type)}>
                              {getStepIcon(step.type)}
                              <span className="ml-1 capitalize">{step.type}</span>
                            </Badge>
                            <span className="font-medium">{step.title}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{step.content}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm font-medium">Day {step.delayDays}</p>
                          {step.template && (
                            <p className="text-xs text-muted-foreground">{step.template}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
